<template>
  <SectionKvBannerSingle01
    v-if="itemData"
    :breadcrumbs="breadcrumbs"
    :title="itemData.title"
    :description="itemData.description"
    :button01="button01"
    :button02="button02"
    :wistia-code="itemData.wistiaCode"
    :thumbnail="contextThumbnail"
    :theme="theme"
    @click-button="handleClickButton"
  />
</template>

<script setup lang="ts">
  import type { TThemeStyle } from '@/assets/types/Solutions';
  import type { IButton } from '@/components/Shared/Section/KvBanner/Single01.vue';

  interface IBanner {
    title: string;
    description: string;
    button: IButton;
    wistiaCode?: string;
    themeStyle: TThemeStyle;
  }

  interface IProps {
    solutionAlias: string;
    itemData: IBanner;
  }

  const props = defineProps<IProps>();

  const emit = defineEmits<{
    (e: 'click-request-demo'): void;
  }>();

  const route = useRoute();
  const { t, locale } = useI18n();
  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);

  const { isCN } = useLanguage();
  const { provideFormatUrl } = useFormatUrl();
  const { provideResponsiveImgUrl } = useSolutionsData();

  const { solution, generic } = route.params;

  const breadcrumbs = computed((): string[] =>
    ['Home', 'Solutions', solution as string, generic as string].filter(Boolean)
  );

  const theme = computed((): TTheme => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'green';
      case 'connected':
        return 'cyan';
      case 'ai':
        return 'purple';
      case 'operations':
        return 'yellow';
      default:
        return 'blue';
    }
  });

  const contextThumbnail = computed((): string => {
    return provideResponsiveImgUrl('banner');
  });

  const button01 = computed(
    (): IButton => ({
      text: t('solutions.request-a-demo'),
      link: `/${locale.value}/marketplace/solutions/${props.solutionAlias}/request-demo`,
      trackingCode: isCN.value ? 'tracking_requestdemo' : undefined,
    })
  );

  const button02 = computed((): IButton | undefined => {
    if (props.itemData.button?.text) {
      return {
        ...props.itemData.button,
        link: provideFormatUrl(props.itemData.button.link),
      };
    }
    return undefined;
  });

  const handleClickButton = (index: number): void => {
    if (index === 0) {
      emit('click-request-demo');
    }
  };

  export type { IBanner, IButton };
</script>
